import { useState, useEffect } from "react";
import Sidebar from "../Sidebar/Sidebar";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
	Box,
	Button,
	Chip,
	InputAdornment,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
	Paper,
	Menu,
	MenuItem,
	FormControlLabel,
	Checkbox,
} from "@mui/material";
import { Search, Add } from "@mui/icons-material";
import AddCompanyModal from "./components/AddCompany";
import instance from "../../apiServices/instance";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

const columnOptions = [
	{ key: "startDate", label: "Start Date" },
	{ key: "dueDate", label: "Due Date" },
	{ key: "status", label: "Status" },
];

const statusColorMap = {
	pending: "#FFC107",
	"AML & KYC": "#ffd6ba",
	"Professional Clearance": "#ceffcf",
	"Onboarding Email Sent": "#fc9999",
};

const Onboarding = () => {
	const [search, setSearch] = useState("");
	const [showAddCompanyModal, setShowAddCompanyModal] = useState(false);
	const [columnVisibility, setColumnVisibility] = useState({
		startDate: true,
		dueDate: true,
		status: true,
	});
	const [anchorEl, setAnchorEl] = useState(null);
	const [companies, setCompanies] = useState([]);

	const toggleColumn = (key) => {
		setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
	};

	const resetFilters = () => {
		setSearch("");
		setColumnVisibility({
			startDate: true,
			dueDate: true,
			status: true,
		});
	};

	const hasFilters =
		search.trim() !== "" ||
		Object.values(columnVisibility).some((isVisible) => !isVisible);

	const filteredData = companies.filter((row) =>
		row.companyName?.toLowerCase().includes(search.toLowerCase())
	);

	const fetchCompanies = async () => {
		try {
			const res = await instance.get("/api/company-info");
			if (res.status === 200) setCompanies(res.data.data);
		} catch (error) {
			console.error("Error fetching companies: ", error);
		}
	};

	const navigate = useNavigate();

	useEffect(() => {
		fetchCompanies();
	}, []);

	return (
		<Box
			display='flex'
			sx={{
				height: "100vh",
				backgroundColor: "#F6F5F8",
				color: "#2F2D3B",
			}}>
			{showAddCompanyModal && (
				<AddCompanyModal
					showModal={showAddCompanyModal}
					closeModal={() => setShowAddCompanyModal(false)}
					fetchCompanies={fetchCompanies}
				/>
			)}
			<Box sx={{ flexGrow: 1 }}>
				<Sidebar />
			</Box>
			<Box sx={{ p: 3, width: "85%" }}>
				{/* Header */}
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						mb: 2,
					}}>
					<Typography variant='h5' fontWeight='bold'>
						Onboarding
					</Typography>
					<Button
						variant='contained'
						size='large'
						sx={{
							backgroundColor: "#c2c1c3",
							color: "#000",
							padding: "0.75rem 2.5rem",
						}}
						onClick={() => setShowAddCompanyModal(true)}
						startIcon={<Add />}>
						Add New Company
					</Button>
				</Box>

				{/* Search & Filters */}
				<Box sx={{ display: "flex", gap: 2, mb: 2 }}>
					<TextField
						variant='outlined'
						size='small'
						placeholder='Search...'
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						InputProps={{
							startAdornment: (
								<InputAdornment position='start'>
									<Search />
								</InputAdornment>
							),
						}}
					/>
					<Button
						variant='outlined'
						onClick={(e) => setAnchorEl(e.currentTarget)}
						endIcon={<KeyboardArrowDownIcon />}>
						Filters
					</Button>

					{hasFilters && (
						<Button
							variant='contained'
							onClick={resetFilters}
							endIcon={<CloseIcon />}>
							Active Filter
						</Button>
					)}
				</Box>

				{/* Column Selection Dropdown */}
				<Menu
					anchorEl={anchorEl}
					open={Boolean(anchorEl)}
					onClose={() => setAnchorEl(null)}>
					{columnOptions.map((col) => (
						<MenuItem key={col.key}>
							<FormControlLabel
								control={
									<Checkbox
										checked={columnVisibility[col.key]}
										onChange={() => toggleColumn(col.key)}
									/>
								}
								label={col.label}
							/>
						</MenuItem>
					))}
				</Menu>

				{/* Table */}
				<TableContainer component={Paper}>
					<Table>
						<TableHead>
							<TableRow sx={{ backgroundColor: "#f3f3f3" }}>
								<TableCell>
									<strong>Client Name</strong>
								</TableCell>
								{columnVisibility.startDate && (
									<TableCell>
										<strong>Start Date</strong>
									</TableCell>
								)}
								{columnVisibility.dueDate && (
									<TableCell>
										<strong>Due Date</strong>
									</TableCell>
								)}
								{columnVisibility.status && (
									<TableCell>
										<strong>Status</strong>
									</TableCell>
								)}
							</TableRow>
						</TableHead>
						<TableBody>
							{filteredData.map((row, index) => (
								<TableRow
									key={index}
									onClick={() =>
										navigate(`/onboarding/${row.id}`)
									}
									sx={{
										cursor: "pointer",
										"&:hover": {
											backgroundColor: "#f5f5f5",
										},
									}}>
									<TableCell>
										{row.companyName || "N/A"}
									</TableCell>

									{columnVisibility.startDate && (
										<TableCell>
											{row.createdAt
												? dayjs(row.createdAt).format(
														"YYYY-MM-DD"
												  )
												: "N/A"}
										</TableCell>
									)}

									{columnVisibility.dueDate && (
										<TableCell>
											{row.updatedAt ? (
												<Chip
													label={(() => {
														const dueDate = dayjs(
															row.updatedAt
														);
														const today = dayjs();
														const diffDays =
															dueDate.diff(
																today,
																"day"
															);

														if (diffDays > 15)
															return `Due in ${diffDays} days`;
														if (diffDays >= 0)
															return `Due in ${diffDays} days`;
														return "Overdue";
													})()}
													sx={{
														backgroundColor:
															(() => {
																const dueDate =
																	dayjs(
																		row.updatedAt
																	);
																const today =
																	dayjs();
																const diffDays =
																	dueDate.diff(
																		today,
																		"day"
																	);

																if (
																	diffDays >
																	15
																)
																	return "#4CAF50"; // Green
																if (
																	diffDays >=
																	0
																)
																	return "#FFC107"; // Yellow
																return "#F44336"; // Red
															})(),
														color: "#000",
														borderRadius: "75px",
														width: "150px",
														textAlign: "center",
													}}
												/>
											) : (
												"N/A"
											)}
										</TableCell>
									)}

									{columnVisibility.status && (
										<TableCell>
											<Chip
												label={
													row.status
														? row.status
														: "Pending"
												}
												sx={{
													backgroundColor: row.status
														? statusColorMap[
																row.status
														  ]
														: statusColorMap.pending,
													color: "#000",
													borderRadius: "0.5rem",
													width: "230px",
												}}
											/>
										</TableCell>
									)}
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			</Box>
		</Box>
	);
};

export default Onboarding;
